import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Divider,
  Grid,
  Button,
  Avatar,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  TextField,
  Snackbar,
  Alert,
  IconButton,
  Chip,
  LinearProgress,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import {
  CardMembership,
  Person,
  Edit,
  Save,
  Close,
  WorkspacePremium,
  Email,
  CalendarMonth,
  Lock,
  School,
  LinkedIn,
  Domain,
  Work,
  Badge,
  Wc,
  Visibility,
} from '@mui/icons-material';
import { getCurrentUser, setCurrentUser } from '../utils/auth.js';
import {
  getUserCourses,
  getCertificateTest,
  getTrilhaAcompanhamento,
  updateUserData,
  getTiposInstituicao,
  getAreasAtuacao,
  getCargos,
  getCertificateData,
} from '../services/api.js';
import { useNavigate } from 'react-router-dom';
import ImgCapaCurso from '../assets/img_capa_curso.png';

const StudentAreaPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('informacoes');
  const [user, setUser] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Edit Mode States
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    data_nascimento: '',
    genero: '',
    id_tipo_instituicao: '',
    id_cargo: '',
    id_area_atuacao: '',
    password: '',
    oldPassword: '',
  });

  const [tiposInstituicao, setTiposInstituicao] = useState([]);
  const [areasAtuacao, setAreasAtuacao] = useState([]);
  const [cargos, setCargos] = useState([]);

  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const types = await getTiposInstituicao();
        setTiposInstituicao(Array.isArray(types) ? types : []);
      } catch (err) {
        console.error('Error fetching institution types:', err);
      }
    };
    fetchDropdownData();
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const currentUser = getCurrentUser();
        setUser(currentUser);
        setFormData({
          nome: currentUser?.nm_usuario || '',
          email: currentUser?.nm_email || '',
          data_nascimento: currentUser?.dt_nascimento
            ? new Date(currentUser.dt_nascimento).toISOString().split('T')[0]
            : '',
          genero: currentUser?.cd_genero || '',
          id_tipo_instituicao: currentUser?.id_tipo_instituicao || '',
          id_cargo: currentUser?.id_cargo || '',
          id_area_atuacao: currentUser?.id_area_atuacao || '',
          password: '',
          oldPassword: '',
        });

        if (currentUser?.id_tipo_instituicao) {
          getAreasAtuacao(currentUser.id_tipo_instituicao)
            .then(res => setAreasAtuacao(Array.isArray(res) ? res : []))
            .catch(() => { });
          getCargos(currentUser.id_tipo_instituicao)
            .then(res => setCargos(Array.isArray(res) ? res : []))
            .catch(() => { });
        }

        const response = await getUserCourses();
        const userCoursesRaw = response?.cursos_usuario || [];

        const coursesWithProgress = await Promise.all(
          userCoursesRaw.map(async item => {
            try {
              const progressData = await getTrilhaAcompanhamento(
                item.id_curso_usuario
              );
              return {
                ...item.curso,
                id_curso: item.id_curso || item.curso?.id_curso || item.curso?.id || item.id,
                id_curso_usuario: item.id_curso_usuario,
                id_curso_versao:
                  item.id_curso_versao ||
                  item.curso?.id_curso_versao ||
                  item.curso?.versoes?.[0]?.id_curso_versao ||
                  null,
                progresso_geral: progressData?.progresso_geral || 0,
                dt_conclusao: item.dt_fim,
                modalidade: item.sg_modalidade,
                imagem: item.curso.image || item.curso.imagem,
                titulo: item.curso.nm_titulo || item.curso.titulo,
                descricao: item.curso.nm_descricao || item.curso.descricao,
              };
            } catch (err) {
              console.error(
                `Error fetching progress for course ${item.id_curso}:`,
                err
              );
              return {
                ...item.curso,
                id_curso: item.id_curso || item.curso?.id_curso || item.curso?.id || item.id,
                id_curso_usuario: item.id_curso_usuario,
                id_curso_versao:
                  item.id_curso_versao ||
                  item.curso?.id_curso_versao ||
                  item.curso?.versoes?.[0]?.id_curso_versao ||
                  null,
                progresso_geral: 0,
                modalidade: item.sg_modalidade,
                imagem: item.curso.image || item.curso.imagem,
              };
            }
          })
        );

        setCourses(coursesWithProgress);
      } catch (error) {
        console.error('Error loading student data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const handleDropdownChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'id_tipo_instituicao'
        ? { id_area_atuacao: '', id_cargo: '' }
        : {}),
    }));

    if (name === 'id_tipo_instituicao') {
      if (value) {
        getAreasAtuacao(value)
          .then(res => setAreasAtuacao(Array.isArray(res) ? res : []))
          .catch(() => { });
        getCargos(value)
          .then(res => setCargos(Array.isArray(res) ? res : []))
          .catch(() => { });
      } else {
        setAreasAtuacao([]);
        setCargos([]);
      }
    }
  };

  const handleDownloadCertificate = async (
    courseId,
    courseTitle,
    courseVersionId
  ) => {
    try {
      if (!user?.id_usuario) return;
      const blob = await getCertificateTest(
        user.id_usuario,
        courseId,
        courseVersionId
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Certificado - ${courseTitle}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao baixar certificado:', error);
      setNotification({
        open: true,
        message: 'Erro ao baixar certificado. Tente novamente.',
        severity: 'error',
      });
    }
  };

  const handleViewCertificate = async (course) => {
    const currentUser = getCurrentUser();
    const userId = currentUser?.id_usuario || currentUser?.id;
    const courseId = course?.id_curso || course?.curso?.id_curso || course?.id_curso_usuario || course?.id;
    if (!userId || !courseId) {
      setNotification({ open: true, message: `Usuário ou curso não identificado. User: ${userId}, Course: ${courseId}`, severity: 'error' });
      return;
    }

    try {
      const data = await getCertificateData(userId, courseId);
      let validationCode = data?.validation_code;
      if (!validationCode && Array.isArray(data) && data.length > 0) {
        validationCode = data[0]?.validation_code;
      } else if (!validationCode && data?.results && Array.isArray(data.results) && data.results.length > 0) {
        validationCode = data.results[0]?.validation_code;
      }

      if (validationCode) {
        const certificateUrl = `${window.location.origin}/certificados/validar/${validationCode}`;
        window.open(certificateUrl, '_blank');
      } else {
        setNotification({ open: true, message: 'Certificado ainda não disponível ou código não encontrado.', severity: 'error' });
      }
    } catch (err) {
      console.error("Erro ao obter dados do certificado", err);
      setNotification({ open: true, message: 'Erro ao buscar o certificado.', severity: 'error' });
    }
  };

  const handleShareLinkedIn = async (course) => {
    const currentUser = getCurrentUser();
    const userId = currentUser?.id_usuario || currentUser?.id;
    const courseId = course?.id_curso || course?.curso?.id_curso || course?.id_curso_usuario || course?.id;
    const courseTitle = course?.titulo || course?.curso?.nm_titulo || 'Curso';
    if (!userId || !courseId) {
      setNotification({ open: true, message: `Usuário ou curso não identificado. User: ${userId}, Course: ${courseId}`, severity: 'error' });
      return;
    }

    try {
      const data = await getCertificateData(userId, courseId);
      let validationCode = data?.validation_code;
      if (!validationCode && Array.isArray(data) && data.length > 0) {
        validationCode = data[0]?.validation_code;
      } else if (!validationCode && data?.results && Array.isArray(data.results) && data.results.length > 0) {
        validationCode = data.results[0]?.validation_code;
      }

      if (!validationCode) {
        setNotification({ open: true, message: 'Certificado ainda não disponível para compartilhamento.', severity: 'error' });
        return;
      }

      const certificateUrl = `${window.location.origin}/certificados/validar/${validationCode}`;

      const postText = `🎓 Concluí o curso Operação e Manutenção de Reatores Fischer–Tropsch, que integra o projeto Produção de Combustível Sustentável de Aviação (SAF) a partir de Resíduos de Biomassa.\n\nA formação combinou conteúdos técnicos e atividades práticas em um simulador imersivo, proporcionando uma experiência prática em operação, manutenção, segurança e monitoramento das condições operacionais de reatores Fischer-Tropsch.\n\nO curso integra uma iniciativa realizada em parceria pela Geo Biogas & Carbon e pela Deutsche Gesellschaft für Internationale Zusammenarbeit (GIZ), com desenvolvimento e estruturação sob responsabilidade da 4Control. O projeto conta com o apoio da Finep e é implementado com recursos do programa develoPPP, do Ministério Federal da Cooperação Econômica e do Desenvolvimento da Alemanha (BMZ).\n\nConfira meu certificado aqui: ${certificateUrl}\n\n#FischerTropsch #SAF #OperaçãoIndustrial #CombustíveisSustentáveis`;

      const shareUrl = `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(
        postText
      ).replace(/%26/g, '%2526')}`;
      window.open(shareUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error("Erro ao gerar link do LinkedIn", err);
    }
  };

  const handleUpdateProfile = async () => {
    try {
      if (!user?.id_usuario) return;

      // Prepare data for update
      const payload = {
        nm_usuario: formData.nome,
        nm_email: formData.email,
        dt_nascimento: formData.data_nascimento,
        cd_genero: formData.genero,
        id_tipo_instituicao: formData.id_tipo_instituicao ? Number(formData.id_tipo_instituicao) : null,
        id_cargo: formData.id_cargo ? Number(formData.id_cargo) : null,
        id_area_atuacao: formData.id_area_atuacao ? Number(formData.id_area_atuacao) : null,
      };

      // Only add password fields if user is trying to change it
      if (formData.password) {
        if (!formData.oldPassword) {
          setNotification({
            open: true,
            message: 'Informe a senha atual para alterar a senha.',
            severity: 'warning',
          });
          return;
        }
        payload.pw_senha = formData.password;
        payload.pw_senha_antiga = formData.oldPassword;
      }

      await updateUserData(user.id_usuario, payload);

      // Update local state and cookie
      const updatedUser = {
        ...user,
        nm_usuario: formData.nome,
        nm_email: formData.email,
        dt_nascimento: formData.data_nascimento,
        cd_genero: formData.genero,
        id_tipo_instituicao: formData.id_tipo_instituicao ? Number(formData.id_tipo_instituicao) : null,
        id_cargo: formData.id_cargo ? Number(formData.id_cargo) : null,
        id_area_atuacao: formData.id_area_atuacao ? Number(formData.id_area_atuacao) : null,
      };

      setUser(updatedUser);
      setCurrentUser(updatedUser);

      // Clear sensitive fields
      setFormData(prev => ({
        ...prev,
        password: '',
        oldPassword: '',
      }));

      setIsEditing(false);
      setNotification({
        open: true,
        message: 'Perfil atualizado com sucesso!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Update error:', error);
      setNotification({
        open: true,
        message: error.message || 'Falha ao atualizar perfil.',
        severity: 'error',
      });
    }
  };

  const completedCourses = courses.filter(c => c.progresso_geral === 100);

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    switch (activeTab) {
      case 'informacoes':
        return (
          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            <Typography
              variant='h5'
              fontWeight='bold'
              gutterBottom
              sx={{ color: '#003B46', mb: 3, textAlign: 'center' }}
            >
              Minhas Informações
            </Typography>

            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                border: '1px solid #E0E4E7',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              {/* Cover Area */}
              <Box
                sx={{
                  height: 140,
                  background:
                    'linear-gradient(45deg, #006064 30%, #00BCD4 90%)',
                }}
              />

              {/* Edit Button (Floating) */}
              {!isEditing ? (
                <Button
                  startIcon={<Edit />}
                  variant='contained'
                  onClick={() => setIsEditing(true)}
                  sx={{
                    position: 'absolute',
                    top: 160,
                    right: 24,
                    borderRadius: 2,
                    textTransform: 'none',
                    bgcolor: 'white',
                    color: '#003B46',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    '&:hover': {
                      bgcolor: '#F5F5F5',
                    },
                  }}
                >
                  Editar
                </Button>
              ) : (
                <IconButton
                  onClick={() => setIsEditing(false)}
                  sx={{
                    position: 'absolute',
                    top: 160,
                    right: 24,
                    bgcolor: '#FFEBEE',
                    color: '#D32F2F',
                    '&:hover': {
                      bgcolor: '#FFCDD2',
                    },
                  }}
                >
                  <Close />
                </IconButton>
              )}

              <Box sx={{ px: 4, pb: 4 }}>
                {/* Header Section (Avatar + Name) */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center', // Center everything
                    mt: -6, // Negative margin to overlap cover
                    mb: 4,
                  }}
                >
                  <Avatar
                    sx={{
                      width: 120,
                      height: 120,
                      bgcolor: 'white', // Border effect
                      p: 0.5, // Border thickness
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      mb: 2,
                    }}
                  >
                    <Avatar
                      sx={{
                        width: '100%',
                        height: '100%',
                        bgcolor: 'primary.main',
                        fontSize: 48,
                      }}
                    >
                      {user?.nm_usuario?.charAt(0) || 'U'}
                    </Avatar>
                  </Avatar>

                  <Typography
                    variant='h5'
                    fontWeight='800'
                    sx={{ color: '#003B46' }}
                  >
                    {isEditing ? 'Editando Perfil' : user?.nm_usuario}
                  </Typography>
                  <Typography variant='body1' color='text.secondary'>
                    {user?.nm_email}
                  </Typography>
                </Box>

                <Divider sx={{ mb: 4 }} />

                {/* Details Sections */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

                  {/* Section 1: Dados Pessoais */}
                  <Paper
                    variant='outlined'
                    sx={{
                      p: 3,
                      bgcolor: isEditing ? '#F9FAFB' : 'white',
                      borderRadius: 2,
                      border: isEditing ? '1px dashed #E0E4E7' : '1px solid #E0E4E7',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, color: '#003B46' }}>
                      <Person sx={{ mr: 1 }} />
                      <Typography variant='subtitle1' fontWeight='bold'>
                        Dados Pessoais
                      </Typography>
                    </Box>

                    <Grid container spacing={3}>
                      {/* Nome */}
                      <Grid item xs={12} sm={6} md={6}>
                        <Typography variant='subtitle2' fontWeight='bold' color='#003B46' mb={1}>
                          Nome Completo
                        </Typography>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            size='small'
                            value={formData.nome}
                            onChange={e =>
                              setFormData({ ...formData, nome: e.target.value })
                            }
                            sx={{ bgcolor: 'white' }}
                          />
                        ) : (
                          <Typography variant='body1' color='text.secondary'>
                            {user?.nm_usuario}
                          </Typography>
                        )}
                      </Grid>

                      {/* Email */}
                      <Grid item xs={12} sm={6} md={6}>
                        <Typography variant='subtitle2' fontWeight='bold' color='#003B46' mb={1}>
                          Email
                        </Typography>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            size='small'
                            value={formData.email}
                            onChange={e =>
                              setFormData({ ...formData, email: e.target.value })
                            }
                            sx={{ bgcolor: 'white' }}
                          />
                        ) : (
                          <Typography variant='body1' color='text.secondary' sx={{ wordBreak: 'break-all' }}>
                            {user?.nm_email}
                          </Typography>
                        )}
                      </Grid>

                      {/* Data Nascimento */}
                      <Grid item xs={12} sm={6} md={6}>
                        <Typography variant='subtitle2' fontWeight='bold' color='#003B46' mb={1}>
                          Data de Nascimento
                        </Typography>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            type='date'
                            size='small'
                            value={formData.data_nascimento}
                            onChange={e =>
                              setFormData({
                                ...formData,
                                data_nascimento: e.target.value,
                              })
                            }
                            InputLabelProps={{ shrink: true }}
                            sx={{ bgcolor: 'white' }}
                          />
                        ) : (
                          <Typography variant='body1' color='text.secondary'>
                            {user?.dt_nascimento
                              ? new Date(user.dt_nascimento).toLocaleDateString()
                              : '-'}
                          </Typography>
                        )}
                      </Grid>

                      {/* Gênero */}
                      <Grid item xs={12} sm={6} md={6}>
                        <Typography variant='subtitle2' fontWeight='bold' color='#003B46' mb={1}>
                          Gênero
                        </Typography>
                        {isEditing ? (
                          <FormControl fullWidth size='small' sx={{ bgcolor: 'white' }}>
                            <Select
                              name='genero'
                              value={formData.genero}
                              onChange={handleDropdownChange}
                              displayEmpty
                            >
                              <MenuItem value=''>Selecione o Gênero</MenuItem>
                              <MenuItem value='M'>Masculino</MenuItem>
                              <MenuItem value='F'>Feminino</MenuItem>
                              <MenuItem value='O'>Outro</MenuItem>
                              <MenuItem value='N'>Prefiro não informar</MenuItem>
                            </Select>
                          </FormControl>
                        ) : (
                          <Typography variant='body1' color='text.secondary'>
                            {user?.cd_genero === 'M' ? 'Masculino' : user?.cd_genero === 'F' ? 'Feminino' : user?.cd_genero === 'O' ? 'Outro' : user?.cd_genero === 'N' ? 'Prefiro não informar' : '-'}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Section 2: Perfil Profissional */}
                  <Paper
                    variant='outlined'
                    sx={{
                      p: 3,
                      bgcolor: isEditing ? '#F9FAFB' : 'white',
                      borderRadius: 2,
                      border: isEditing ? '1px dashed #E0E4E7' : '1px solid #E0E4E7',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, color: '#003B46' }}>
                      <Work sx={{ mr: 1 }} />
                      <Typography variant='subtitle1' fontWeight='bold'>
                        Perfil Profissional
                      </Typography>
                    </Box>

                    <Grid container spacing={3}>
                      {/* Tipo Instituição */}
                      <Grid item xs={12} md={4}>
                        <Typography variant='subtitle2' fontWeight='bold' color='#003B46' mb={1}>
                          Tipo de Instituição
                        </Typography>
                        {isEditing ? (
                          <FormControl fullWidth size='small' sx={{ bgcolor: 'white' }}>
                            <Select
                              name='id_tipo_instituicao'
                              value={formData.id_tipo_instituicao}
                              onChange={handleDropdownChange}
                              displayEmpty
                            >
                              <MenuItem value=''>Selecione a Instituição</MenuItem>
                              {tiposInstituicao.map(t => (
                                <MenuItem key={t.id_tipo_instituicao} value={t.id_tipo_instituicao}>
                                  {t.nm_tipo_instituicao}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <Typography variant='body1' color='text.secondary'>
                            {tiposInstituicao.find(t => t.id_tipo_instituicao === user?.id_tipo_instituicao)?.nm_tipo_instituicao || '-'}
                          </Typography>
                        )}
                      </Grid>

                      {/* Área de Atuação */}
                      <Grid item xs={12} md={4}>
                        <Typography variant='subtitle2' fontWeight='bold' color='#003B46' mb={1}>
                          Área de Atuação
                        </Typography>
                        {isEditing ? (
                          <FormControl fullWidth size='small' sx={{ bgcolor: 'white' }}>
                            <Select
                              name='id_area_atuacao'
                              value={formData.id_area_atuacao}
                              onChange={handleDropdownChange}
                              displayEmpty
                              disabled={!formData.id_tipo_instituicao || areasAtuacao.length === 0}
                            >
                              <MenuItem value=''>Selecione a Área</MenuItem>
                              {areasAtuacao.map(a => (
                                <MenuItem key={a.id_area_atuacao} value={a.id_area_atuacao}>
                                  {a.nm_area_atuacao}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <Typography variant='body1' color='text.secondary'>
                            {areasAtuacao.find(a => a.id_area_atuacao === user?.id_area_atuacao)?.nm_area_atuacao || '-'}
                          </Typography>
                        )}
                      </Grid>

                      {/* Cargo */}
                      <Grid item xs={12} md={4}>
                        <Typography variant='subtitle2' fontWeight='bold' color='#003B46' mb={1}>
                          Cargo
                        </Typography>
                        {isEditing ? (
                          <FormControl fullWidth size='small' sx={{ bgcolor: 'white' }}>
                            <Select
                              name='id_cargo'
                              value={formData.id_cargo}
                              onChange={handleDropdownChange}
                              displayEmpty
                              disabled={!formData.id_tipo_instituicao || cargos.length === 0}
                            >
                              <MenuItem value=''>Selecione o Cargo</MenuItem>
                              {cargos.map(c => (
                                <MenuItem key={c.id_cargo} value={c.id_cargo}>
                                  {c.nm_cargo}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <Typography variant='body1' color='text.secondary'>
                            {cargos.find(c => c.id_cargo === user?.id_cargo)?.nm_cargo || '-'}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Section 3: Segurança (Edit Only) */}
                  {isEditing && (
                    <Paper
                      variant='outlined'
                      sx={{
                        p: 3,
                        bgcolor: '#F9FAFB',
                        borderRadius: 2,
                        border: '1px dashed #E0E4E7',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, color: '#003B46' }}>
                        <Lock sx={{ mr: 1 }} />
                        <Typography variant='subtitle1' fontWeight='bold'>
                          Segurança
                        </Typography>
                      </Box>

                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant='subtitle2' fontWeight='bold' color='#003B46' mb={1}>
                            Senha Atual
                          </Typography>
                          <TextField
                            fullWidth
                            size='small'
                            type='password'
                            placeholder='Obrigatório para alterar'
                            value={formData.oldPassword}
                            onChange={e =>
                              setFormData({
                                ...formData,
                                oldPassword: e.target.value,
                              })
                            }
                            sx={{ bgcolor: 'white' }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant='subtitle2' fontWeight='bold' color='#003B46' mb={1}>
                            Nova Senha
                          </Typography>
                          <TextField
                            fullWidth
                            size='small'
                            type='password'
                            placeholder='Digite a nova senha'
                            value={formData.password}
                            onChange={e =>
                              setFormData({
                                ...formData,
                                password: e.target.value,
                              })
                            }
                            sx={{ bgcolor: 'white' }}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  )}
                </Box>

                {isEditing && (
                  <Box
                    sx={{
                      mt: 4,
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: 2,
                    }}
                  >
                    <Button
                      variant='text'
                      color='inherit'
                      onClick={() => setIsEditing(false)}
                      sx={{ fontWeight: 'bold' }}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant='contained'
                      startIcon={<Save />}
                      onClick={handleUpdateProfile}
                      sx={{
                        bgcolor: '#003B46',
                        fontWeight: 'bold',
                        px: 4,
                        '&:hover': { bgcolor: '#00262d' },
                      }}
                    >
                      Salvar
                    </Button>
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>
        );

      case 'cursos':
        return (
          <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
            <Typography
              variant='h4'
              fontWeight='900'
              gutterBottom
              sx={{ color: '#0A2540', mb: 4, textAlign: 'center', letterSpacing: '-0.5px' }}
            >
              Meus Cursos
            </Typography>
            {courses.length > 0 ? (
              <Grid container spacing={4}>
                {courses.map(course => (
                  <Grid item xs={12} sm={6} md={4} key={course.id_curso}>
                    <Card
                      elevation={0}
                      sx={{
                        borderRadius: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        bgcolor: '#FFFFFF',
                        border: '1px solid #EAEAEA',
                        transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        height: '100%',
                        position: 'relative',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
                          borderColor: 'transparent',
                          '& .course-cover': {
                            transform: 'scale(1.08)',
                          },
                          '& .course-overlay': {
                            opacity: 0.4,
                          }
                        },
                      }}
                      onClick={() =>
                        navigate(`/curso/${course.id_curso_usuario}/conteudo`)
                      }
                    >
                      {/* Image Container */}
                      <Box
                        sx={{
                          position: 'relative',
                          overflow: 'hidden',
                          width: '100%',
                          height: 300,
                          flexShrink: 0,
                        }}
                      >
                        <CardMedia
                          component='img'
                          image={course.imagem || ImgCapaCurso}
                          alt={course.titulo}
                          className='course-cover'
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'transform 0.7s ease',
                          }}
                        />
                        {/* Gradient Overlay */}
                        <Box
                          className='course-overlay'
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.7) 100%)',
                            opacity: 0.7,
                            transition: 'opacity 0.4s ease',
                          }}
                        />
                        {/* Modalidade Badge */}
                        <Chip
                          label={
                            course.modalidade === 'R' ? 'Remoto' : 'Híbrido'
                          }
                          size='small'
                          sx={{
                            position: 'absolute',
                            top: 16,
                            left: 16,
                            bgcolor: 'rgba(255, 255, 255, 0.85)',
                            backdropFilter: 'blur(8px)',
                            fontWeight: '800',
                            color: '#0A2540',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            borderRadius: 1.5,
                            px: 1,
                          }}
                        />
                      </Box>

                      {/* Content Container */}
                      <CardContent
                        sx={{
                          p: 3,
                          flexGrow: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          bgcolor: '#FFFFFF',
                        }}
                      >
                        <Box sx={{ mb: 3 }}>
                          <Typography
                            variant='h6'
                            fontWeight='800'
                            gutterBottom
                            sx={{
                              color: '#0A2540',
                              lineHeight: 1.3,
                              mb: 1.5,
                            }}
                          >
                            {course.titulo}
                          </Typography>

                          {course.descricao && (
                            <Typography
                              variant='body2'
                              color='text.secondary'
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                lineHeight: 1.6,
                              }}
                            >
                              {course.descricao}
                            </Typography>
                          )}
                        </Box>

                        <Box sx={{ mt: 'auto' }}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: 1,
                            }}
                          >
                            <Typography
                              variant='caption'
                              fontWeight='800'
                              sx={{ color: '#0A2540' }}
                            >
                              Progresso
                            </Typography>
                            <Typography
                              variant='caption'
                              fontWeight='800'
                              sx={{ color: '#8DC63F' }}
                            >
                              {Math.round(course.progresso_geral)}%
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant='determinate'
                            value={course.progresso_geral}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              bgcolor: '#F0F0F0',
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 4,
                                bgcolor: '#8DC63F',
                              },
                              mb: 3,
                            }}
                          />

                          <Button
                            variant='contained'
                            fullWidth
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(
                                `/curso/${course.id_curso_usuario}/conteudo`
                              );
                            }}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 'bold',
                              borderRadius: 2,
                              py: 1.2,
                              bgcolor: '#0A2540',
                              color: '#FFFFFF',
                              boxShadow: 'none',
                              '&:hover': {
                                bgcolor: '#123B66',
                                boxShadow: '0 6px 16px rgba(10,37,64,0.2)',
                              },
                            }}
                          >
                            Continuar Curso
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Paper
                sx={{
                  p: 6,
                  textAlign: 'center',
                  bgcolor: '#F9FAFB',
                  borderRadius: 4,
                  border: '1px dashed #D1D5DB',
                }}
                elevation={0}
              >
                <School sx={{ fontSize: 48, color: '#9CA3AF', mb: 2 }} />
                <Typography variant='h6' color='text.secondary' fontWeight='bold'>
                  Nenhum curso em andamento
                </Typography>
                <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                  Você ainda não está matriculado em nenhum curso.
                </Typography>
              </Paper>
            )}
          </Box>
        );

      case 'certificados':
        return (
          <Box sx={{ maxWidth: 900, mx: 'auto' }}>
            <Typography
              variant='h4'
              fontWeight='900'
              gutterBottom
              sx={{ color: '#0A2540', mb: 4, textAlign: 'center', letterSpacing: '-0.5px' }}
            >
              Meus Certificados
            </Typography>

            <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
              {completedCourses.length > 0 ? (
                completedCourses.map(course => (
                  <Paper
                    key={course.id_curso}
                    elevation={0}
                    sx={{
                      p: 0,
                      border: '1px solid #EAEAEA',
                      borderRadius: 4,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      bgcolor: '#FFFFFF',
                      position: 'relative',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 16px 32px rgba(0,0,0,0.06)',
                        borderColor: 'transparent',
                      },
                    }}
                  >
                    {/* Left Accent Bar */}
                    <Box sx={{ width: { xs: '100%', sm: 8 }, height: { xs: 8, sm: 'auto' }, background: 'linear-gradient(180deg, #D4AF37 0%, #AA8111 100%)' }} />

                    <Box sx={{ p: 4, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, width: '100%', alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between', gap: 4 }}>
                      <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexGrow: 1 }}>
                        {/* Premium Badge Icon */}
                        <Box
                          sx={{
                            width: 72,
                            height: 72,
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #FDF7E2 0%, #F5E6B4 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            boxShadow: 'inset 0 2px 4px rgba(255,255,255,0.8), 0 4px 12px rgba(212,175,55,0.2)',
                            border: '2px solid #EAD284'
                          }}
                        >
                          <WorkspacePremium sx={{ fontSize: 36, color: '#B8860B' }} />
                        </Box>

                        <Box>
                          <Typography
                            variant='h6'
                            fontWeight='800'
                            sx={{ color: '#0A2540', lineHeight: 1.3, mb: 1 }}
                          >
                            {course.titulo}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Chip
                              label='Certificado Disponível'
                              size='small'
                              sx={{
                                bgcolor: '#F0F9ED',
                                color: '#4CAF50',
                                fontWeight: 'bold',
                                border: '1px solid #C8E6C9'
                              }}
                            />
                            <Typography variant='body2' color='text.secondary' sx={{ fontWeight: 500 }}>
                              Concluído em:{' '}
                              {course.dt_conclusao
                                ? new Date(
                                  course.dt_conclusao
                                ).toLocaleDateString('pt-BR')
                                : 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1.5,
                          flexDirection: 'column',
                          width: { xs: '100%', sm: '240px' },
                          flexShrink: 0,
                        }}
                      >
                        <Button
                          variant='contained'
                          size='large'
                          color='success'
                          fullWidth
                          startIcon={<Visibility />}
                          onClick={() => handleViewCertificate(course)}
                          sx={{
                            whiteSpace: 'nowrap',
                            borderRadius: '12px',
                            textTransform: 'none',
                            fontWeight: 'bold',
                            px: 3,
                          }}
                        >
                          Visualizar Certificado
                        </Button>

                        <Button
                          variant='outlined'
                          size='large'
                          fullWidth
                          startIcon={<WorkspacePremium />}
                          onClick={() =>
                            handleDownloadCertificate(
                              course.id_curso_usuario,
                              course.titulo,
                              course.id_curso_versao
                            )
                          }
                          sx={{
                            whiteSpace: 'nowrap',
                            borderRadius: '12px',
                            textTransform: 'none',
                            fontWeight: 'bold',
                            borderWidth: 2,
                            borderColor: '#0A2540',
                            color: '#0A2540',
                            px: 3,
                            '&:hover': {
                              borderWidth: 2,
                              borderColor: '#123B66',
                              bgcolor: '#F5F8FA',
                            },
                          }}
                        >
                          Baixar PDF
                        </Button>

                        <Button
                          variant='contained'
                          size='large'
                          fullWidth
                          startIcon={<LinkedIn />}
                          onClick={() =>
                            handleShareLinkedIn(course)
                          }
                          sx={{
                            whiteSpace: 'nowrap',
                            borderRadius: '12px',
                            textTransform: 'none',
                            fontWeight: 'bold',
                            bgcolor: '#0A66C2',
                            boxShadow: '0 4px 12px rgba(10,102,194,0.2)',
                            px: 3,
                            '&:hover': {
                              bgcolor: '#004182',
                              boxShadow: '0 6px 16px rgba(10,102,194,0.3)',
                            },
                          }}
                        >
                          Compartilhar
                        </Button>
                      </Box>
                    </Box>
                  </Paper>
                ))
              ) : (
                <Paper
                  sx={{
                    p: 6,
                    textAlign: 'center',
                    bgcolor: '#F9FAFB',
                    borderRadius: 4,
                    border: '1px dashed #D1D5DB',
                  }}
                  elevation={0}
                >
                  <WorkspacePremium
                    sx={{ fontSize: 64, color: '#D1D5DB', mb: 3 }}
                  />
                  <Typography
                    variant='h6'
                    color='text.secondary'
                    gutterBottom
                    fontWeight='bold'
                  >
                    Sua galeria de conquistas está vazia
                  </Typography>
                  <Typography variant='body1' color='text.secondary' sx={{ maxWidth: 400, mx: 'auto' }}>
                    Conclua seus cursos para desbloquear certificados exclusivos e compartilhá-los com sua rede.
                  </Typography>
                </Paper>
              )}
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        minHeight: '100vh',
        pt: { xs: '56px', sm: '64px' },
        bgcolor: '#F9FAFB',
      }}
    >
      {/* Sidebar */}
      <Box
        sx={{
          width: { xs: '100%', md: 280 },
          background: 'linear-gradient(180deg, #FFFFFF 0%, #FAFAFA 100%)',
          borderRight: { xs: 'none', md: '1px solid #E0E4E7' },
          borderBottom: { xs: '1px solid #E0E4E7', md: 'none' },
          pt: { xs: 3, md: 5 },
          pb: { xs: 2, md: 0 },
          pl: { xs: 2, md: 3 },
          pr: { xs: 2, md: 3 },
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography
          variant='h6'
          fontWeight='bold'
          sx={{ mb: { xs: 2, md: 4 }, color: '#003B46', pl: 1, display: { xs: 'none', md: 'block' } }}
        >
          Minha Área
        </Typography>

        <List sx={{ display: { xs: 'flex', md: 'block' }, overflowX: 'auto', gap: { xs: 1, md: 0 }, pb: { xs: 1, md: 0 } }}>
          <ListItem
            button
            selected={activeTab === 'informacoes'}
            onClick={() => setActiveTab('informacoes')}
            sx={{
              borderRadius: 2,
              mb: { xs: 0, md: 1 },
              minWidth: { xs: 'fit-content', md: 'auto' },
              pl: 2,
              py: 1.5,
              bgcolor: activeTab === 'informacoes' ? '#E0F2F1' : 'transparent',
              color: activeTab === 'informacoes' ? '#004D40' : '#546E7A',
              '&:hover': { bgcolor: '#F5F5F5' },
              '&.Mui-selected': {
                bgcolor: '#E0F2F1',
                color: '#00695C',
                '&:hover': { bgcolor: '#B2DFDB' },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
              <Person />
            </ListItemIcon>
            <ListItemText
              primary='Informações'
              primaryTypographyProps={{
                fontWeight: activeTab === 'informacoes' ? 'bold' : 'medium',
              }}
            />
          </ListItem>
          <ListItem
            button
            selected={activeTab === 'cursos'}
            onClick={() => setActiveTab('cursos')}
            sx={{
              borderRadius: 2,
              mb: { xs: 0, md: 1 },
              minWidth: { xs: 'fit-content', md: 'auto' },
              pl: 2,
              py: 1.5,
              bgcolor: activeTab === 'cursos' ? '#E0F2F1' : 'transparent',
              color: activeTab === 'cursos' ? '#004D40' : '#546E7A',
              '&:hover': { bgcolor: '#F5F5F5' },
              '&.Mui-selected': {
                bgcolor: '#E0F2F1',
                color: '#00695C',
                '&:hover': { bgcolor: '#B2DFDB' },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
              <School />
            </ListItemIcon>
            <ListItemText
              primary='Cursos'
              primaryTypographyProps={{
                fontWeight: activeTab === 'cursos' ? 'bold' : 'medium',
              }}
            />
          </ListItem>
          <ListItem
            button
            selected={activeTab === 'certificados'}
            onClick={() => setActiveTab('certificados')}
            sx={{
              borderRadius: 2,
              mb: { xs: 0, md: 1 },
              minWidth: { xs: 'fit-content', md: 'auto' },
              pl: 2,
              py: 1.5,
              bgcolor: activeTab === 'certificados' ? '#E0F2F1' : 'transparent',
              color: activeTab === 'certificados' ? '#004D40' : '#546E7A',
              '&:hover': { bgcolor: '#F5F5F5' },
              '&.Mui-selected': {
                bgcolor: '#E0F2F1',
                color: '#00695C',
                '&:hover': { bgcolor: '#B2DFDB' },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
              <CardMembership />
            </ListItemIcon>
            <ListItemText
              primary='Certificados'
              primaryTypographyProps={{
                fontWeight: activeTab === 'certificados' ? 'bold' : 'medium',
              }}
            />
          </ListItem>
        </List>
      </Box>

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, p: { xs: 3, md: 5, lg: 6 } }}>
        {renderContent()}
      </Box>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StudentAreaPage;
